import { dist2, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, FairWare } from '../types.ts';
import { ev } from './combat.ts';
import { spawnPoint } from './create.ts';
import { stepToward } from './enemies.ts';

/**
 * Fair day (El pozo 3, docs/SAGAS.md): the first fair in forty years, in Musgoviejo's square. The stalls stand round
 * the well with their wares and a lantern each; Fenn's cart is one of them. Draggers come up the roads, take a ware
 * from a stall (Fenn's cart first) and haul it back to their hole; one struck down drops it, and a hero who stays by a
 * dropped ware lifts it and takes it back to its stall. The ink thieves fight the heroes and show only near a
 * lantern. It never ends lost: when its time runs out, what is back at the stalls decides how big the fair is.
 */

const carrying = (s: BattleState, pid: string): FairWare | undefined => s.fair?.wares.find((w) => w.pid === pid);

export const fairTick = (s: BattleState): void => {
  const r = s.rules?.fair;
  if (!r) return;
  const st = (s.fair ??= {
    wares: r.stalls.flatMap((x, i) => Array.from({ length: x.wares }, () => ({ stall: i, x: x.x, y: x.y, at: 'stall' as const, by: 0, pid: '', held: 0 }))),
    lost: 0,
    done: 0,
  });
  if (!s.started || st.done) return;
  for (const w of st.wares) {
    if (w.at === 'lost' || w.at === 'stall') continue;
    if (w.at === 'dragged') {
      // Its dragger fell (or is gone): it drops where it was.
      if (!s.enemies.some((e) => e.id === w.by && e.hp > 0)) {
        w.at = 'ground';
        w.by = 0;
        ev(s, { t: 'fair', kind: 'drop', stall: w.stall, x: w.x, y: w.y });
      }
      continue;
    }
    if (w.at === 'carried') {
      const h = s.heroes.find((x) => x.pid === w.pid);
      if (!h || h.downTicks > 0) {
        w.at = 'ground';
        w.pid = '';
        continue;
      }
      w.x = h.x;
      w.y = h.y;
      const home = r.stalls[w.stall] as { x: number; y: number };
      if (withinRange(h.x, h.y, home.x, home.y, r.reach)) {
        w.at = 'stall';
        w.pid = '';
        w.x = home.x;
        w.y = home.y;
        ev(s, { t: 'fair', kind: 'back', stall: w.stall, x: home.x, y: home.y });
      }
      continue;
    }
    // On the ground: a hero with free hands who stays by it lifts it.
    const by = s.heroes.find((h) => h.downTicks === 0 && !carrying(s, h.pid) && withinRange(h.x, h.y, w.x, w.y, r.reach));
    w.held = by ? w.held + 1 : 0;
    if (by && w.held >= r.lift) {
      w.at = 'carried';
      w.pid = by.pid;
      w.held = 0;
    }
  }
  if (s.waveT >= r.out) {
    st.done = s.tick;
    ev(s, { t: 'fair', kind: 'end', stall: -1, x: 0, y: 0 });
  }
};

/** A dragger at the fair: to the nearest ware it can take (Fenn's cart first), then back to its hole with it; it never fights. */
export const fairDrag = (s: BattleState, e: Enemy, px: number): void => {
  const r = s.rules?.fair;
  const st = s.fair;
  if (!r || !st) return;
  const held = st.wares.find((w) => w.at === 'dragged' && w.by === e.id);
  if (held) {
    const hole = spawnPoint(s, e.sector);
    e.anim = 'walk';
    if (stepToward(e, hole.x, hole.y, mulBp(px, r.dragBp))) {
      held.at = 'lost';
      st.lost++;
      e.hp = 0;
      ev(s, { t: 'fair', kind: 'lost', stall: held.stall, x: e.x, y: e.y });
      return;
    }
    held.x = e.x;
    held.y = e.y;
    return;
  }
  const free = st.wares.filter((w) => w.at === 'stall' || w.at === 'ground');
  const cart = free.filter((w) => w.stall === r.cart);
  let best: FairWare | undefined;
  let bd = 0x7fffffff;
  for (const w of cart.length ? cart : free) {
    const d = dist2(e.x, e.y, w.x, w.y);
    if (d < bd) {
      bd = d;
      best = w;
    }
  }
  if (!best) {
    e.anim = 'idle';
    return;
  }
  e.anim = 'walk';
  if (stepToward(e, best.x, best.y, px)) {
    best.at = 'dragged';
    best.by = e.id;
    ev(s, { t: 'fair', kind: 'taken', stall: best.stall, x: best.x, y: best.y });
  }
};

/** The fair's lanterns light what is near them (the ink thieves show only there, or by a hero's). */
export const fairLit = (s: BattleState, x: number, y: number): boolean => {
  const r = s.rules?.fair;
  if (!r) return false;
  for (const l of r.stalls) if (withinRange(x, y, l.x, l.y, r.light)) return true;
  for (const h of s.heroes) if (h.downTicks === 0 && withinRange(x, y, h.x, h.y, r.lantern)) return true;
  return false;
};

/** How many wares are back at the stalls, and of how many. */
export const fairSaved = (s: BattleState): { saved: number; of: number } => {
  const w = s.fair?.wares ?? [];
  return { saved: w.filter((x) => x.at === 'stall').length, of: w.length };
};

/** Its time run out: always won (what was saved says how big the fair is). */
export const fairResult = (s: BattleState): 'won' | null => {
  const r = s.rules?.fair;
  return r && s.fair?.done && s.tick - s.fair.done >= r.beat ? 'won' : null;
};
